import api from "../utils/api";
import { detectPlatform, isStandalone } from "./platform";
import { finishSignOut, forgetDevice, rememberDevice, signOutBusy, signOutCleanup } from "./pushSignOut";

/**
 * Notifications on the phone: reminders before a talk, changes the guidance office
 * makes to one, and "Your results are ready". The server sends them
 * (backend custom_auth/push_notifications.py); src/service-worker.js shows them and
 * keeps the count on the app icon.
 *
 * ANNA never asks on page load. It asks in the tap that makes the reason obvious
 * ("Book session"), right after one ("You're booked", the last test) and on the
 * Profile page (PushAsk.js).
 * iPhones only allow it inside the installed app (iOS 16.4+), so in Safari those
 * places point to "Get the app" instead.
 *
 * Phones and tablets only: computers at school are shared, so a computer never offers
 * notifications and one that has them turns them off quietly (email covers reminders
 * there). Signing out turns them off too (pushSignOut.js).
 */

export const PUSH = {
  // A computer, a browser that can't, or a server that sends none (also `npm start`, which has no service worker)
  UNSUPPORTED: "unsupported",
  // iPhone or iPad outside the installed app
  NEEDS_APP: "needs-app",
  // The person said no; only the phone's settings can change that
  BLOCKED: "blocked",
  OFF: "off",
  ON: "on",
  // Turning on didn't work in this browser (its push service refused or didn't answer)
  FAILED: "failed",
};

const QUIET = { skipErrorToast: true };
const READY_TIMEOUT_MS = 4000;
const SUBSCRIBE_TIMEOUT_MS = 20000;
// askInTap: the phone's question stays up until the person answers it
const ASK_TIMEOUT_MS = 120000;

// The service worker and server key from the last check, so a tap can ask the phone
// without waiting for anything (askInTap).
let cached = { registration: null, key: "" };
/** Tests only: forget the last check. */
export const resetPushCache = () => {
  cached = { registration: null, key: "" };
  keyRequest = null;
};

// People who turned notifications on in this browser, so a subscription the browser
// replaced on its own is quietly sent again the next time they open ANNA.
const TURNED_ON_KEY = "annaPush.turnedOn";

const readList = () => {
  try {
    const list = JSON.parse(window.localStorage.getItem(TURNED_ON_KEY) || "[]");
    return Array.isArray(list) ? list : [];
  } catch (error) {
    return [];
  }
};
const writeList = (list) => {
  try {
    window.localStorage.setItem(TURNED_ON_KEY, JSON.stringify(list.slice(-20)));
  } catch (error) {
    // Storage blocked: nothing else depends on it.
  }
};

/** The signed-in person's id, read from the sign-in token (no request needed). */
const signedInId = () => {
  try {
    const token = window.localStorage.getItem("authToken");
    const payload = JSON.parse(window.atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload.user_id != null ? String(payload.user_id) : null;
  } catch (error) {
    return null;
  }
};

const remember = (on) => {
  const id = signedInId();
  if (!id) return;
  const others = readList().filter((item) => item !== id);
  writeList(on ? [...others, id] : others);
};
const turnedOnBefore = () => {
  const id = signedInId();
  return Boolean(id) && readList().includes(id);
};

/**
 * iPhones and iPads before iOS 16.4 have no web notifications, installed app or not (N7).
 * The version comes from "OS 16_3 like Mac OS X", or Safari's "Version/16.3" for an iPad
 * that asks for desktop sites. Unknown versions count as new enough.
 */
export const iosTooOldForPush = (ua = typeof navigator !== "undefined" ? navigator.userAgent : "") => {
  if (!detectPlatform(ua).ios) return false;
  const match = String(ua).match(/OS (\d+)_(\d+)/) || String(ua).match(/Version\/(\d+)\.(\d+)/);
  if (!match) return false;
  const major = Number(match[1]);
  const minor = Number(match[2]);
  return major < 16 || (major === 16 && minor < 4);
};

export const browserCanPush = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window &&
  !iosTooOldForPush();

/** iPhone or iPad Safari (or another iPhone browser): notifications only work in the installed app. */
export const pushNeedsApp = () => detectPlatform().ios && !isStandalone() && !iosTooOldForPush();

/** Notifications are for phones and tablets; school computers are shared. */
export const isPhone = () => detectPlatform().mobile;

let keyRequest = null;
/** The server's key, or "" when it sends no notifications. Asked once per page load. */
export const loadPushKey = () => {
  if (!keyRequest) {
    keyRequest = api
      .get("/api/push/key/", QUIET)
      .then(({ data }) => {
        cached.key = (data?.ready && data.public_key) || "";
        return cached.key;
      })
      .catch(() => {
        keyRequest = null;
        return "";
      });
  }
  return keyRequest;
};

const keyBytes = (base64url) => {
  const padded = `${base64url}${"=".repeat((4 - (base64url.length % 4)) % 4)}`.replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(padded);
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
};

const sameKey = (subscription, key) => {
  const current = subscription?.options?.applicationServerKey;
  if (!current) return true;
  const wanted = keyBytes(key);
  const have = new Uint8Array(current);
  return have.length === wanted.length && have.every((byte, index) => byte === wanted[index]);
};

/** The service worker's registration, or null (development, or it isn't ready in time). */
async function registration() {
  if (!browserCanPush()) return null;
  const existing = await navigator.serviceWorker.getRegistration();
  if (!existing) return null;
  const reg = await Promise.race([
    navigator.serviceWorker.ready,
    new Promise((resolve) => setTimeout(() => resolve(null), READY_TIMEOUT_MS)),
  ]);
  if (reg) cached.registration = reg;
  return reg;
}

async function currentSubscription() {
  const reg = await registration();
  return reg ? reg.pushManager.getSubscription() : null;
}

const withTimeout = (promise, ms) =>
  Promise.race([promise, new Promise((resolve, reject) => setTimeout(() => reject(new Error("timed out")), ms))]);

async function subscribe(key) {
  // A sign-out still dropping the old subscription must not drop this one.
  await signOutCleanup();
  const reg = await registration();
  if (!reg) return null;
  let subscription = await reg.pushManager.getSubscription();
  // A subscription made with an older server key can't receive anything any more.
  if (subscription && !sameKey(subscription, key)) {
    await subscription.unsubscribe().catch(() => {});
    subscription = null;
  }
  if (!subscription) {
    subscription = await withTimeout(
      reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) }),
      SUBSCRIBE_TIMEOUT_MS,
    );
  }
  await saveSubscription(subscription);
  return subscription;
}

async function saveSubscription(subscription) {
  await api.post("/api/push/subscription/", subscription.toJSON(), QUIET);
  // Kept so a sign-out can tell the server without waiting for anything
  rememberDevice(subscription.endpoint, api.defaults.baseURL);
}

/** Tells the server what went wrong, so a phone that won't turn on shows up in its logs. */
const reportProblem = (step, error) => {
  api
    .post("/api/push/problem/", { step, name: String(error?.name || ""), message: String(error?.message || error || "") }, QUIET)
    .catch(() => {});
};

/** A computer that has notifications (from before they were phones only) drops them quietly. */
async function turnOffOnComputer() {
  if (isPhone() || !browserCanPush() || Notification.permission !== "granted") return;
  try {
    const subscription = await currentSubscription();
    if (!subscription) return;
    if (window.localStorage.getItem("authToken")) {
      await api.delete("/api/push/subscription/", { ...QUIET, data: { endpoint: subscription.endpoint } }).catch(() => {});
    }
    await subscription.unsubscribe();
    forgetDevice();
  } catch (error) {
    // Tried again on the next page load
  }
}

/** Where this browser stands for the signed-in person (one of PUSH). */
export async function getPushState() {
  if (!isPhone()) {
    turnOffOnComputer();
    return PUSH.UNSUPPORTED;
  }
  if (pushNeedsApp()) return PUSH.NEEDS_APP;
  if (!browserCanPush()) return PUSH.UNSUPPORTED;
  if (!(await registration())) return PUSH.UNSUPPORTED;
  const key = await loadPushKey();
  if (!key) return PUSH.UNSUPPORTED;
  if (Notification.permission === "denied") return PUSH.BLOCKED;
  if (Notification.permission !== "granted") return PUSH.OFF;

  try {
    const subscription = await currentSubscription();
    if (subscription) {
      const { data } = await api.post("/api/push/status/", { endpoint: subscription.endpoint }, QUIET);
      if (data?.on) {
        rememberDevice(subscription.endpoint, api.defaults.baseURL);
        return PUSH.ON;
      }
    }
    // On before, but the browser replaced its subscription (or the server dropped it): send it again.
    if (turnedOnBefore()) {
      await subscribe(key);
      return PUSH.ON;
    }
  } catch (error) {
    // Couldn't check; offer the switch again.
  }
  return PUSH.OFF;
}

async function finishTurnOn(key) {
  try {
    if (!(await subscribe(key))) return PUSH.UNSUPPORTED;
    remember(true);
    return PUSH.ON;
  } catch (error) {
    console.warn("Couldn't turn on notifications:", error);
    reportProblem("subscribe", error);
    return PUSH.FAILED;
  }
}

/**
 * Asks the phone and signs up in one step, with nothing awaited first. pushManager.subscribe()
 * shows the phone's "Allow notifications?" question itself, so the sign-up happens while
 * the tap still counts: iPhones refuse one that comes after the person has answered.
 * Call it straight from a tap. Null when it can't ask this way: not a phone that can, the
 * phone was already answered, or this page hasn't checked yet (getPushState).
 */
export function askInTap() {
  const { registration: reg, key } = cached;
  if (!reg || !key || !isPhone() || pushNeedsApp() || !browserCanPush()) return null;
  if (Notification.permission !== "default" || signOutBusy()) return null;
  let subscribing;
  try {
    subscribing = reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) });
  } catch (error) {
    subscribing = Promise.reject(error);
  }
  return withTimeout(subscribing, ASK_TIMEOUT_MS)
    .then(async (subscription) => {
      // One made with an older server key was in the way: the long way replaces it.
      if (!sameKey(subscription, key)) return finishTurnOn(key);
      await saveSubscription(subscription);
      remember(true);
      return PUSH.ON;
    })
    .catch((error) => {
      if (Notification.permission === "denied") return PUSH.BLOCKED;
      // Closed without an answer
      if (Notification.permission !== "granted") return PUSH.OFF;
      // Allowed, but this sign-up didn't go through: once more the long way.
      reportProblem("ask-in-tap", error);
      return finishTurnOn(key);
    });
}

/**
 * Turns notifications on, asking the phone first. Call it straight from a tap: phones
 * only show their "Allow notifications?" question from one. Resolves to the new state.
 */
export async function turnOnPush() {
  if (!isPhone() || !browserCanPush()) return PUSH.UNSUPPORTED;
  const asked = askInTap();
  if (asked) return asked;
  // Ask before anything else is awaited, while the tap still counts.
  const asking = Notification.permission === "granted" ? Promise.resolve("granted") : Notification.requestPermission();
  const [permission, key] = await Promise.all([asking, loadPushKey()]);
  if (permission === "denied") return PUSH.BLOCKED;
  if (permission !== "granted") return PUSH.OFF;
  if (!key) return PUSH.UNSUPPORTED;
  return finishTurnOn(key);
}

/** Turns notifications off in this browser. Resolves to PUSH.OFF. */
export async function turnOffPush() {
  remember(false);
  forgetDevice();
  try {
    const subscription = await currentSubscription();
    if (subscription) {
      await api.delete("/api/push/subscription/", { ...QUIET, data: { endpoint: subscription.endpoint } }).catch(() => {});
      await subscription.unsubscribe().catch(() => {});
    }
    if (navigator.clearAppBadge) await navigator.clearAppBadge().catch(() => {});
  } catch (error) {
    // Already off as far as this browser can tell.
  }
  return PUSH.OFF;
}

/** The Results page opened: clear the app icon count and the "results ready" notifications. */
export async function markResultsOpened() {
  if (!browserCanPush() || Notification.permission !== "granted") return;
  try {
    if (navigator.clearAppBadge) navigator.clearAppBadge().catch(() => {});
    const reg = await registration();
    if (reg) (await reg.getNotifications({ tag: "results" })).forEach((item) => item.close());
    await api.post("/api/push/results-seen/", {}, QUIET);
  } catch (error) {
    // The count comes back right with the next notification.
  }
}

// Moves the app to a page without reloading it, the way the app's own links do.
const openPage = (url) => {
  if (typeof url !== "string" || !url.startsWith("/") || url.startsWith("//")) return;
  if (window.location.pathname + window.location.search === url) return;
  const index = window.history.state?.idx;
  window.history.pushState(
    { usr: null, key: Math.random().toString(36).slice(2, 10), idx: typeof index === "number" ? index + 1 : 0 },
    "",
    url,
  );
  window.dispatchEvent(new PopStateEvent("popstate", { state: window.history.state }));
};

/**
 * Called once from index.js. Tapping a notification while ANNA is open moves it to that
 * page; after someone signs in, this browser stops getting the previous person's
 * notifications. It also finishes an interrupted sign-out and turns a computer's off.
 */
export function startPushListeners() {
  if (!browserCanPush()) return;
  finishSignOut();
  turnOffOnComputer();
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type === "anna:open-page") openPage(event.data.url);
  });
  window.addEventListener("loginSuccess", () => {
    if (isPhone() && Notification.permission === "granted") getPushState().catch(() => {});
  });
}
